import type { AssessmentResultsDocument } from '@app/types/assessment-results/interfaces'
import type { ScopeMode } from '@utils/stores/scoped-entry'

import { useAssessmentResultsStore } from '@stores/assessment-results'
import { useDisciplineStore } from '@stores/discipline'

import { useCsRoutes } from '@composables/routes/cs-route.hook'

import { isNotFoundError, show404, showRequestError } from '@utils/pages/error'

interface AssessmentResultApiReturn {
  fetchAssessmentResult: (assessmentId: number, mode: ScopeMode) => Promise<void>
}

export const useAssessmentResultApi = (): AssessmentResultApiReturn => {
  const { $axios } = useNuxtApp()
  const assessmentResultsStore = useAssessmentResultsStore()
  const disciplineStore = useDisciplineStore()

  const requestResult = async (url: string): Promise<AssessmentResultsDocument> => {
    const { data } = await $axios.get(url)

    return data?.data as AssessmentResultsDocument
  }

  const fetchAssessmentResult = async (assessmentId: number, mode: ScopeMode): Promise<void> => {
    const disciplineId = disciplineStore.activeDiscipline?.id

    if (!disciplineId) {
      console.warn('missing disciplineId for assessment-results/show.hook.ts')

      return
    }

    const url = useCsRoutes('disciplines.assessment-result', {
      '[discipline_id]': disciplineId,
      '[assessment_id]': assessmentId,
    })

    if (!url) {
      console.warn('missing url for assessment-results/show.hook.ts')

      return
    }

    const requestId = assessmentResultsStore.beginResult(assessmentId, mode)

    try {
      assessmentResultsStore.settleResult(assessmentId, requestId, await requestResult(url))
    } catch (err: unknown) {
      console.error('unable to retrieve assessment-results/show.hook.ts:', err)

      assessmentResultsStore.failResult(assessmentId, requestId)

      if (isNotFoundError(err)) show404()
      else showRequestError()
    }
  }

  return { fetchAssessmentResult }
}
